import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Dashboard from './components/Dashboard';
import MemberRegistration from './components/MemberRegistration';
import MemberEdit from './components/MemberEdit';
import HealthInformation from './components/HealthInformation';
import BodyAssessment from './components/BodyAssessment';
import Members from './components/Members';
import Login from './components/Login';
import AttendanceUpload from './components/AttendanceUpload';
import AttendanceDashboard from './components/AttendanceDashboard';
import PaymentMemberSelect from './components/PaymentMemberSelect';
import PaymentRecord from './components/PaymentRecord';
import PaymentsDashboard from './components/PaymentsDashboard';
import PaymentAnalytics from './components/PaymentAnalytics';
import FinanceDashboard from './components/FinanceDashboard';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentReceiptPublic from './components/PaymentReceiptPublic';
import BodyMeasurementsPanel from './components/BodyMeasurementsPanel';
import WorkoutPlan from './components/WorkoutPlan';
import WorkoutPlanShared from './components/WorkoutPlanShared';
import WorkoutCopy from './components/WorkoutCopy';
import AppLayout from './components/layout/AppLayout';
import { TokenService } from './services/tokenService';
import 'react-toastify/dist/ReactToastify.css';

// Create auth context
export interface AuthContextType {
  isAuthenticated: boolean | null;
  setIsAuthenticated: (value: boolean) => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: null,
  setIsAuthenticated: () => {}
});

// Auth Provider component
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = () => {
      // Check if we have a valid JWT token
      const isValidToken = TokenService.isTokenValid();
      const hasRefreshToken = TokenService.hasRefreshToken();
      
      // If we have a valid token or a refresh token, consider authenticated
      setIsAuthenticated(isValidToken || hasRefreshToken);
    };
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route wrapper component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();

  if (isAuthenticated === null) {
    return (
      <div className="u-app-loading">
        <div className="fitness-loader">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to="/login" replace state={{ from: location }} />
  );
};

// Public Route wrapper component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();

  if (isAuthenticated === null) {
    return (
      <div className="u-app-loading">
        <div className="fitness-loader">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return !isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to="/dashboard" replace state={{ from: location }} />
  );
};

const AuthenticatedLayout: React.FC = () => {
  const { setIsAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    TokenService.clearTokens();
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  return (
    <ProtectedRoute>
      <AppLayout onLogout={handleLogout} />
    </ProtectedRoute>
  );
};

function App() {
  return (
    <div className="u-app-root">
      <Router>
        <AuthProvider>
          <Routes>
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route
              path="/"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route element={<AuthenticatedLayout />}>
              <Route path="/dashboard/*" element={<Dashboard />} />
              <Route path="/register" element={<MemberRegistration />} />
              <Route path="/members/:id/edit" element={<MemberEdit />} />
              <Route path="/register/health" element={<HealthInformation />} />
              <Route path="/assessment" element={<BodyAssessment />} />
              <Route path="/members" element={<Members />} />
              <Route path="/attendance/upload" element={<AttendanceUpload />} />
              <Route path="/attendance/dashboard" element={<AttendanceDashboard />} />
              <Route path="/payments" element={<PaymentMemberSelect />} />
              <Route path="/payments/record/:memberId" element={<PaymentRecord />} />
              <Route path="/payments/edit/:paymentId" element={<PaymentRecord />} />
              <Route path="/payments/success" element={<PaymentSuccess />} />
              <Route path="/payments/dashboard" element={<PaymentsDashboard />} />
              <Route path="/payments/analytics" element={<PaymentAnalytics />} />
              <Route path="/finance" element={<FinanceDashboard />} />
              <Route path="/members/:memberId/measurements" element={<BodyMeasurementsPanel />} />
              <Route path="/workout-plan/:memberId" element={<WorkoutPlan />} />
              <Route path="/workout-plan/:memberId/:workoutId" element={<WorkoutPlan />} />
              <Route path="/copy-workouts" element={<WorkoutCopy />} />
            </Route>
            <Route path="/receipt/:token" element={<PaymentReceiptPublic />} />
            <Route path="/workout-share/:token" element={<WorkoutPlanShared />} />
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </AuthProvider>
      </Router>
    </div>
  );
}

export default App;
